import { Fragment, useCallback, useMemo, useState } from "react";
import { useFeedsModulesStore } from "@/common/store/feedsModulesStore";
import { GifPreviewPlaceholder } from "@/components/UI/Skeleton";
import { FeedTypeEnum, IGif, INicheFeedbackResponse, TAddFeedback } from "@/common/services/types";
import { InfinitePlayerScroll } from "@/components/player-feed/infiniteScroll";
import { GifPreview } from "@/components/gifPreview/GifPreview";
import { FeedModules } from "@/components/player-feed/FeedModules";
import { findFeedModules } from "./utils/findFeedModules";

interface IProps {
  gifs: IGif[];
  boostedGifs?: IGif[];
  rel?: string;
  isLoading?: boolean;
  isError?: boolean;
  fetchMore: () => Promise<void>;
  hasMore: boolean;
  addNicheFeedback?: TAddFeedback;
  nicheFeedbacks?: INicheFeedbackResponse[];
  feed: FeedTypeEnum;
}

const PLACEHOLDER_ITEMS_LENGTH = 6;

export const InjectionFeed = (
  {
    gifs,
    boostedGifs,
    rel,
    isLoading,
    isError,
    fetchMore,
    hasMore,
    addNicheFeedback,
    nicheFeedbacks,
    feed,
  }: IProps
) => {
  const [hiddenGifs, setHiddenGifs] = useState<string[]>([]);
  const feedModules = useFeedsModulesStore((state) => state.modules);
  const feedModulesLoading = useFeedsModulesStore((state) => state.loading);

  const filteredGifs = useMemo(() => {
    return gifs.filter((gif) => !hiddenGifs.includes(gif.id));
  }, [hiddenGifs, gifs]);

  const hideGif = useCallback((id: string) => {
    setHiddenGifs((prev) => [...prev, id]);
  }, []);

  const renderGifs = useMemo(() => {
    if (isError) {
      return <p className="error">Error loading gifs. Please refresh the page</p>;
    }

    if (!filteredGifs.length && isLoading && feedModulesLoading) {
      return new Array(PLACEHOLDER_ITEMS_LENGTH).fill("").map((___, index) => (
        <Fragment key={index}>
          <GifPreviewPlaceholder />
        </Fragment>
      ));
    }

    if (!filteredGifs.length && !isLoading && !hasMore) {
      return <p className="error">No gifs to show on this page.</p>;
    }

    const foundModulesCounter: {[key: string]: number} = {};

    const items = filteredGifs.map((gif: IGif, index: number) => {
      const modules = findFeedModules({ feedModules, feed, position: index });
      const moduleEntries = modules?.map((module) => {
        const counter = (foundModulesCounter[module.type] ?? 0) + 1;
        foundModulesCounter[module.type] = counter;

        return {
          module,
          counter,
        };
      }) ?? null;

      return (
        <Fragment key={index}>
          <GifPreview
            key={gif.id}
            gif={gif}
            rel={rel}
            position={index + 1}
            hideGif={hideGif}
            addNicheFeedback={addNicheFeedback}
            nicheFeedbacks={nicheFeedbacks}
          />

          <FeedModules
            key={`feed-module-${index}`}
            modules={moduleEntries}
            gifs={gifs}
            boostedGifs={boostedGifs}
            position={index + 1}
          />
        </Fragment>
      );
    });

    return items;
  }, [
    filteredGifs,
    boostedGifs,
    hideGif,
    isLoading,
    isError,
    rel,
    fetchMore,
    hasMore,
    feed,
    feedModulesLoading
  ]);

  return (
    <InfinitePlayerScroll
      fetchMore={fetchMore}
      hasMore={hasMore}
    >
      {renderGifs}
    </InfinitePlayerScroll>
  );
};
